import React, { useRef } from "react";
import { useSelectState } from "@react-stately/select";
import { InputVariantTypes } from "@ecologi/shared";
import { Item } from "@react-stately/collections";
import { HiddenSelect, AriaSelectOptions, useSelect } from "@react-aria/select";
import { css } from "@emotion/react";
import { isIOS } from "react-device-detect";
import { useButton } from "@react-aria/button";
import styled from "@emotion/styled";
import DownArrow from "@ecologi/assets/icons/FormFields/down_arrow.svg";
import UpwardsArrowIcon from "@ecologi/assets/icons/FormFields/up_arrow.svg";
import { Typography } from "../Typography";
import { theme } from "../theme";
import { ListBoxPopup } from "./ListBoxPopup";
import {
  BackgroundColourMapper,
  ErrorMessage,
  HelpMessage,
} from "../TextField/TextField.styles";
import { NativeSelect } from "./NativeSelect";

export type SelectItemType = {
  label: string;
  value: string;
  isDisabled?: boolean;
};

export type SelectProps = {
  name: string;
  label: string;
  isInvalid?: boolean;
  items: SelectItemType[];
  value: string;
  onChange: (event: SelectEvent) => void;
  hiddenLabel?: boolean;
  "data-testid"?: string;
  errorText?: string | false;
  helpText?: string;
  variant?: InputVariantTypes;
  width?: string;
} & Omit<AriaSelectOptions<any>, "children">;

//-- Select state variant styles

export const ErrorStyles = css`
  border: solid 2px ${theme.colors.scarlet};
  padding-left: 14px; // take away some padding to accommodate left/right border
  padding-right: 16px;
`;

export const DisabledStyles = css`
  border-bottom: solid 2px #c9d0d8 !important;
  cursor: not-allowed;
  color: #c9d0d8 !important;
`;

export type CustomSelectProps = {
  isInvalid?: boolean;
  isDisabled?: boolean;
  variant?: InputVariantTypes;
  isFocused?: boolean;
  isSelected?: boolean;
  isOpen?: boolean;
  width?: string;
};

//this gets the selected styled for the input when isSelected is true
export const getSelectedCSS = (props: CustomSelectProps) => css`
  border: solid 2px ${theme.colors.night};
  outline: none;
  padding-left: 14px; // take away some padding to accommodate left/right border
  padding-right: 14px;

  ${props.isInvalid && ErrorStyles};
`;

const Wrapper = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
`;

export const LabelWrapper = styled.div<{ hiddenLabel?: boolean }>`
  display: ${(props) => (props.hiddenLabel ? "none" : null)};
  margin-bottom: 10px;
`;

export const Label = styled(Typography)<{ isDisabled?: boolean }>`
  color: ${(props) => props.isDisabled && props.theme.colors.cloud};
`;

export const SelectedOption = styled(Typography)<{ isDisabled?: boolean }>`
  color: ${(props) =>
    props.isDisabled ? props.theme.colors.cloud : props.theme.colors.charcoal};
`;

const Placeholder = styled(Typography)<{
  isInvlaid?: boolean;
  isDisabled?: boolean;
}>`
  color: ${(props) =>
    props.isDisabled
      ? props.theme.colors.cloud
      : props.isInvlaid
      ? props.theme.colors.scarlet
      : props.theme.colors.cloudDark}!important;
`;

const UpwardsArrow = styled(UpwardsArrowIcon)`
  margin-bottom: 3px;
`;

export const Arrow = styled.div<CustomSelectProps>`
  path {
    stroke: ${(props) =>
      (props.isInvalid && props.theme.colors.scarlet) ||
      (props.isDisabled && props.theme.colors.cloudDark)};
  }
`;

export const CustomSelect = styled.button<CustomSelectProps>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: ${({ width }) => width || "100%"};
  overflow: hidden;

  margin-bottom: 10px;
  padding: 0px 16px;
  height: 48px;
  white-space: nowrap;

  border-left: none;
  border-right: none;
  border-top: 2px solid transparent;
  border-bottom: 2px solid ${(props) => props.theme.colors.cloudDarker};
  cursor: pointer;

  &:focus {
    outline: none;
  }

  background-color: ${(props) =>
    props.variant
      ? BackgroundColourMapper[props.variant]
      : BackgroundColourMapper.DEFAULT};

  ${(props) => props.isDisabled && DisabledStyles};
  ${(props) => props.isInvalid && ErrorStyles};

  ${(props) =>
    !props.isDisabled &&
    css`
      &:hover {
        background-color: ${theme.colors.mistDark};
      }
    `}

  ${(props) => props.isFocused && !props.isDisabled && getSelectedCSS(props)};
  ${(props) => props.isInvalid && ErrorStyles};
`;

export type SelectEvent = { target: { name: string; value: string } };

export const Select = (props: SelectProps) => {
  const children = props.items.map((item) => (
    <Item key={item.label}>{item.label}</Item>
  ));

  // Create state based on the incoming props
  const state = useSelectState({
    ...props,
    children,
    selectedKey: props.items.find((item) => item.value === props.value)?.label,
    disabledKeys: props.items
      .filter((item) => item.isDisabled)
      .map((item) => item.label),
    onSelectionChange: (label) => {
      props.onChange({
        target: {
          value: props.items.find((item) => item.label === label)?.value || "",
          name: props.name,
        },
      });
    },
  });

  // Get props for child elements from useSelect
  const triggerRef = useRef<HTMLButtonElement>(null);

  const { labelProps, triggerProps, valueProps, menuProps } = useSelect(
    {
      validationState: props.isInvalid ? "invalid" : "valid",
      // @ts-ignore
      children,
      ...props,
    },
    state,
    triggerRef
  );

  // Get props for the button based on the trigger props from useSelect
  const { buttonProps } = useButton(triggerProps, triggerRef);

  return (
    <>
      {isIOS ? (
        <NativeSelect
          items={props.items}
          label={props.label}
          name={props.name}
          isDisabled={props.isDisabled}
          isInvalid={props.isInvalid}
          helpText={props.helpText}
          value={props.value}
          variant={props.variant}
          errorText={props.errorText}
          onChange={props.onChange}
          hiddenLabel={props.hiddenLabel}
        />
      ) : (
        <Wrapper>
          <LabelWrapper hiddenLabel={props.hiddenLabel}>
            <Label
              as="label"
              use="textInputLabel"
              isDisabled={props.isDisabled}
              {...labelProps}
            >
              {props.label}
            </Label>
          </LabelWrapper>
          <HiddenSelect
            isDisabled={props.isDisabled}
            state={state}
            triggerRef={triggerRef}
            label={props.label}
          />
          <CustomSelect
            {...buttonProps}
            ref={triggerRef}
            isSelected={state.isOpen}
            isInvalid={props.isInvalid && !state.isOpen && !state.isFocused}
            data-testid={props["data-testid"]}
            name={props.name}
            isDisabled={props.isDisabled}
            isFocused={state.isFocused && !state.isOpen}
            variant={props.variant}
            width={props.width}
          >
            {state.selectedItem ? (
              <SelectedOption
                use="body1"
                as="p"
                isDisabled={props.isDisabled}
                {...valueProps}
              >
                {state.selectedItem.rendered}
              </SelectedOption>
            ) : (
              <Placeholder use="body1" as="p" isDisabled={props.isDisabled}>
                Select an option
              </Placeholder>
            )}
            <Arrow
              isInvalid={
                props.isInvalid &&
                !state.isOpen &&
                !props.isDisabled &&
                !state.isFocused
              }
              isDisabled={props.isDisabled}
            >
              {state.isOpen && !props.isDisabled ? (
                <UpwardsArrow />
              ) : (
                <DownArrow aria-hidden="true" />
              )}
            </Arrow>
          </CustomSelect>
          {state.isOpen && !props.isDisabled && (
            <ListBoxPopup
              hiddenLabel={props.hiddenLabel}
              {...menuProps}
              state={state}
              isInvalid={props.isInvalid}
              data-testid={`${props["data-testid"]}-quantity`}
            />
          )}
          {props.isInvalid ? (
            <ErrorMessage use="label" as="label">
              {props.errorText && !state.isFocused
                ? props.errorText
                : `Please select ${props.label}`}
            </ErrorMessage>
          ) : (
            props.helpText && (
              <HelpMessage use="label" as="label">
                {props.helpText}
              </HelpMessage>
            )
          )}
        </Wrapper>
      )}{" "}
    </>
  );
};
